import React, {useState} from 'react';

import {
  Anchor,
  Banner,
  Heading,
  Icon,
  SegmentedControl,
  Text,
  WithTooltip
} from '@albert-io/atomic';
import RestrictFreePracticeModal from 'client/Classrooms/Classroom/Teacher/Subjects/RestrictFreePracticeModal.react';
import {SubjectsListCard} from 'client/Classrooms/Classroom/Teacher/Subjects/SubjectsListCard.react';
import {EmptyIllustration, EmptyState} from 'client/TeacherDashboard/components/EmptyState';
import {SubjectsEmptyState} from 'client/TeacherDashboard/components/SubjectsEmptyState';
import LoadingIndicator from 'generic/LoadingIndicator.react';

import {ErrorRetryState} from '../../components/ErrorRetryState';
import {SubjectsFilterMenu} from './SubjectsFilterMenu';
import {useClassSubjects} from './useClassSubjects';

interface Props {
  classroomId: string;
}

type SubjectsTab = 'all' | 'independent';

// (i) tooltip copy carried over verbatim from the legacy TeacherSubjectsList heading tooltips.
const ALL_SUBJECTS_TOOLTIP = <p className='mb-x0!'>Subjects practiced by your students.</p>;
const INDEPENDENT_PRACTICE_TOOLTIP = (
  <p className='mb-x0!'>
    If a subject has open access, that means students can independently practice and view solutions
    for any question in that subject&apos;s Practice tab. Otherwise, students can only practice and
    view solutions from questions that you&apos;ve assigned to them.
    <br />
    <br />
    Close access by selecting a subject below. Open access to more subjects from our Subjects
    library.
  </p>
);

const TabInfo = ({content}: {content: React.ReactNode}) => (
  <WithTooltip as='span' content={content} placement='top' theme='light'>
    <Icon color='tertiary' icon='info-circle' iconStyle='regular' size='inherit' />
  </WithTooltip>
);

// Shown when the class's subjects are ALL hidden and the Show-hidden filter is off: the grid is empty
const HiddenSubjectsEmptyState = () => (
  <EmptyState
    illustration={
      <EmptyIllustration badgeClassName='background-bold content-tertiary' icon='eye-slash' />
    }
  >
    <div className='gap-x1 flex flex-col'>
      <Text as='div' size='l'>
        All subjects are hidden
      </Text>
      <Text as='div' className='max-w-[380px]' color='tertiary'>
        Use Filters to show hidden subjects.
      </Text>
    </div>
  </EmptyState>
);

// v2 per-class Subjects tab (Figma 4746-78638): the colored subject-card grid with per-card
// Open/Close access + partial credit (the legacy SubjectsListCard) and a Filters popover for hidden
// subjects. Flux-free port of TeacherSubjectsList. Licensed teachers at licensed schools get the
// All Subjects / Independent Practice tabs (gated on canManageAccess, mirroring the legacy menu).
export const ClassSubjects = ({classroomId}: Props) => {
  const classSubjects = useClassSubjects(classroomId);
  const [tab, setTab] = useState<SubjectsTab>('all');

  if (classSubjects.hasError) {
    return (
      <div className='pt-x4'>
        <ErrorRetryState
          copy='We couldn’t load this class’s subjects.'
          subtitle='Something went wrong. Check your connection and try again.'
          onRetry={classSubjects.reload}
        />
      </div>
    );
  }

  // A retry refetch can leave isReady true while the classroom is still a stub (no getSubjects);
  // keep showing the spinner until it's a real model rather than flashing empty or crashing.
  if (!classSubjects.isReady || typeof classSubjects.classroom?.getSubjects !== 'function') {
    return <LoadingIndicator />;
  }

  const {classroom} = classSubjects;
  if (classSubjects.hasNoSubjects || !classroom) {
    return <SubjectsEmptyState />;
  }
  // Empty from a stale cache while the revisit refetch is in flight — wait rather than
  // flash a heading over a bare grid (all-hidden keeps the grid so Filters stays reachable).
  if (classSubjects.subjects.length === 0 && !classSubjects.hasHiddenSubjects) {
    return <LoadingIndicator />;
  }

  const showTabs = classSubjects.canManageAccess;
  const isIndependentTab = showTabs && tab === 'independent';
  const visibleSubjects = isIndependentTab
    ? classSubjects.subjects.filter(
        (subject) => !subject.getClassroomRelationships(classroomId).getRestrictFreePractice()
      )
    : classSubjects.subjects;

  return (
    <div className='gap-x3 pt-x4 flex flex-col'>
      <Heading as='h2' className='m-x0' size='s'>
        Subjects
      </Heading>
      {(showTabs || classSubjects.hasHiddenSubjects) && (
        <div className='gap-x2 flex flex-wrap items-center justify-between'>
          {showTabs ? (
            <SegmentedControl
              buttonClassName='font-normal'
              selectedIdx={isIndependentTab ? 1 : 0}
              onSelect={(idx) => setTab(idx === 1 ? 'independent' : 'all')}
              options={[
                <span key='all' className='gap-x1 flex items-center'>
                  All Subjects
                  <TabInfo content={ALL_SUBJECTS_TOOLTIP} />
                </span>,
                <span key='independent' className='gap-x1 flex items-center'>
                  Independent Practice
                  <TabInfo content={INDEPENDENT_PRACTICE_TOOLTIP} />
                </span>
              ]}
            />
          ) : (
            <span />
          )}
          {classSubjects.hasHiddenSubjects && (
            <SubjectsFilterMenu
              showHidden={classSubjects.showHidden}
              onToggle={classSubjects.setShowHidden}
            />
          )}
        </div>
      )}
      {visibleSubjects.length === 0 ? (
        isIndependentTab ? (
          <div className='py-x6 flex flex-col items-center text-center'>
            <Text color='secondary'>
              Students can only practice on questions you&apos;ve assigned to them. If you want to
              allow students to answer any question in a subject,{' '}
              <Anchor to='/subjects/high-school/test-prep'>visit the Library tab</Anchor> and toggle
              on access.
            </Text>
          </div>
        ) : (
          // Every subject is hidden and the Show-hidden filter is off
          <HiddenSubjectsEmptyState />
        )
      ) : (
        <>
          {isIndependentTab && (
            <Banner>
              <Banner.Icon icon='info' iconStyle='solid' />
              <span>
                Open access to more subjects from the{' '}
                <Anchor to='/subjects/high-school/test-prep'>Library</Anchor>
              </span>
            </Banner>
          )}
          <div className='gap-x3 s:grid-cols-2 m:grid-cols-3 grid grid-cols-1'>
            {visibleSubjects.map((subject) => (
              <SubjectsListCard
                key={subject.getId()}
                classroom={classroom}
                subject={subject}
                isAdaptive={subject.isAdaptive()}
                isHidden={subject.isHidden()}
                showActions
                handleCloseAccess={classSubjects.requestCloseAccess}
                handleOpenAccess={classSubjects.openAccess}
                handlePartialCreditToggled={classSubjects.togglePartialCredit}
              />
            ))}
          </div>
        </>
      )}
      {classSubjects.pendingClose && (
        <RestrictFreePracticeModal
          fromClassroom
          handleClose={classSubjects.cancelCloseAccess}
          onRestrict={classSubjects.confirmCloseAccess}
        />
      )}
    </div>
  );
};
